import { InfoCircleOutlined } from '@ant-design/icons'
import MembershipProfile from '@components/membership/membership-profile/MembershipProfile'
import MembershipSubscription from '@components/membership/subscription/MembershipSubscription'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { User } from '@components/user/User'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { MembershipProfile as MembershipProfileModel } from '@models/membership/MembershipProfile'
import { subscriptionMembersStatuses } from '@models/membership/Subscription'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getMembershipProfiles,
  selectMembershipProfiles,
  setMembershipProfileQuery,
} from '@store/membership/MembershipProfileReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { numberFormat } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { formatSnakeCaseLabel } from '@utils/string'
import {
  Col,
  Flex,
  Input,
  Segmented,
  TableColumnsType,
  Tag,
  Tooltip,
} from 'antd'

const MembershipProfilesPage = () => {
  const dispatch = useAppDispatch()
  const membershipProfiles = useAppSelector(selectMembershipProfiles())
  const { query, isLoading } = useAppSelector(
    (state) => state.membershipProfile
  )

  const columns: (TableColumnsType<MembershipProfileModel>[number] & {
    displayOnlyFor?: 'active' | 'cancelled'
  })[] = [
    {
      title: 'Member',
      fixed: 'left',
      render: (record: MembershipProfileModel) => (
        <MembershipProfile.Avatar
          model={record}
          link={record.appUser && record.isActive ? 'app-user' : undefined}
        />
      ),
    },
    {
      title: 'Subscription',
      render: (record: MembershipProfileModel) => (
        <MembershipSubscription.Avatar
          plan
          price
          renewalDate
          coupon
          id={record.subscription}
        />
      ),
    },
    {
      displayOnlyFor: 'active',
      title: 'App user',
      render: (record: MembershipProfileModel) => (
        <>
          {record.appUser && <User.Avatar id={record.appUser} />}
          {!record.appUser && <Tag>{record.code}</Tag>}
        </>
      ),
    },
    {
      displayOnlyFor: 'active',
      title: (
        <Tooltip
          title={
            <Flex vertical>
              <MembershipProfile.WalletDisclaimer style={{ color: 'white' }} />
            </Flex>
          }
        >
          Card number <InfoCircleOutlined />
        </Tooltip>
      ),
      render: (record: MembershipProfileModel) => (
        <MembershipProfile.CardNumberChanger id={record.id} />
      ),
    },
    {
      displayOnlyFor: 'active',
      title: 'Tags',
      render: (record: MembershipProfileModel) => (
        <MembershipProfile.TagManager
          id={record.id}
          onChange={() => fetchMembershipProfiles(params)}
        />
      ),
    },
  ]

  const fetchMembershipProfiles = (params: TableUrlParams) => {
    const statusFromUrl = params.filters.subscriptionStatus || 'active'
    const statusValue = Array.isArray(statusFromUrl)
      ? statusFromUrl[0]
      : statusFromUrl

    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: {
        ...params.filters,
        subscriptionStatus: statusValue,
      },
      search: params.search,
    }
    dispatch(setMembershipProfileQuery(newQuery))
    dispatch(getMembershipProfiles(newQuery))
  }

  const { handleTableChange, handleFiltersChange, params } =
    useTableUrlSync<MembershipProfileModel>({
      onParamsChange: fetchMembershipProfiles,
    })

  // TODO: add 'both' if it has to be showed
  const selectedStatus = String(
    params.filters.subscriptionStatus || 'active'
  ) as 'active' | 'cancelled'

  return (
    <Page
      noWrapWithCol
      permissions={Permission.manageMembersMembership}
      pageTitle={{
        title: 'Members',
        level: 4,
        badge: {
          count: numberFormat(query.pagination?.total ?? 0),
          overflowCount: 999999,
        },
      }}
    >
      <Col span={24}>
        <TableFilters
          initialValues={{
            subscriptionStatus: selectedStatus,
            plans: params.filters.plans?.map(Number),
            tags: params.filters.tags?.map(Number),
            name: params.search.name,
          }}
          onValuesChange={(filters: any, search: any) => {
            if (filters.subscriptionStatus === 'active') {
              delete filters.subscriptionStatus
            }

            handleFiltersChange(filters, search)
          }}
          left={[
            {
              name: 'subscriptionStatus',
              type: 'filter',
              label: 'Subscription status',
              initialValue: 'active',
              input: (
                <Segmented
                  block
                  options={subscriptionMembersStatuses.map((status) => ({
                    value: status,
                    label: formatSnakeCaseLabel(status),
                  }))}
                />
              ),
            },
            {
              name: 'plans',
              type: 'filter',
              label: 'Plans',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '16ch' }}
                  multiple
                  treeSelectProps={{
                    maxTagCount: 'responsive',
                  }}
                  entity={Entity.membershipPlan}
                  placeholder="Select plans"
                />
              ),
            },
            {
              name: 'tags',
              type: 'filter',
              label: 'Tags',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '16ch' }}
                  multiple
                  selectProps={{
                    maxTagCount: 'responsive',
                  }}
                  entity={Entity.membershipProfileTag}
                  placeholder="Select tags"
                />
              ),
            },
          ]}
          right={[
            {
              name: 'name',
              type: 'search',
              label: 'Name',
              input: <Input placeholder="Search member" allowClear />,
            },
          ]}
        />
        <SBTable
          columns={columns.filter(
            (c) => !c.displayOnlyFor || c.displayOnlyFor === selectedStatus
          )}
          dataSource={membershipProfiles}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
          scroll={{ x: 'max-content' }}
        />
      </Col>
    </Page>
  )
}

export default MembershipProfilesPage
